import { useEffect, useRef } from "react";
import type { Editor } from "@tiptap/react";
import type { SlotType } from "@/types/entities/templates";
import {
  transformSlotsFromBackendFormat,
  extractSlotsMap,
} from "../utils/slot-transformations";

interface UseTemplateLoaderOptions {
  richText: string | null | undefined;
  onContentLoaded: (content: string) => void;
  onSlotsExtracted: (slots: Map<string, SlotType>) => void;
  editorRef: React.MutableRefObject<Editor | null>;
  templateType: "document" | "email";
}

export const useTemplateLoader = ({
  richText,
  onContentLoaded,
  onSlotsExtracted,
  editorRef,
  templateType,
}: UseTemplateLoaderOptions) => {
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!richText || hasLoadedRef.current) return;
    const editorContent = transformSlotsFromBackendFormat(richText);

    onContentLoaded(editorContent);
    hasLoadedRef.current = true;

    const initialSlots = extractSlotsMap(editorContent);
    onSlotsExtracted(initialSlots);

    setTimeout(() => {
      if (!editorRef.current) {
        return;
      }

      if (templateType === "document") {
        fixSlotsForDocuments(editorRef.current);
      } else {
        fixSlotsForEmails(editorRef.current);
      }
    }, 200);
  }, [editorRef, onContentLoaded, onSlotsExtracted, richText, templateType]);
};

const fixSlotsForDocuments = (editor: Editor) => {
  const html = editor.getHTML();

  const fixedHtml = html.replace(
    /(\{\{[a-z0-9_-]+(?::[a-z]+)?\}\})(?! )/gi,
    "$1 ",
  );

  if (html !== fixedHtml) {
    editor.commands.setContent(fixedHtml);
  }
};

const fixSlotsForEmails = (editor: Editor) => {
  const { state, view } = editor;
  const { tr, doc } = state;
  let modified = false;

  doc.descendants((node, pos) => {
    if (!node.isText) return;

    const text = node.text || "";

    const slotPattern = /\{\{[a-z0-9_-]+(?::[a-z]+)?\}\}/gi;
    let match;

    while ((match = slotPattern.exec(text)) !== null) {
      const slotEnd = pos + match.index + match[0].length;
      const nextChar = text[match.index + match[0].length];

      if (nextChar !== " ") {
        tr.insertText(" ", slotEnd);
        modified = true;
      }
    }
  });

  if (modified) {
    view.dispatch(tr);
  }
};
